import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import inaPortrait from "@/assets/ina-portrait.jpg";
import { useSEO } from "@/hooks/useCanonical";
import { getStaticRouteSeo } from "@/lib/seo";
import { buildAboutJsonLd } from "@/lib/structuredData";

const About = () => {
  const seo = getStaticRouteSeo("about");

  useSEO({
    ...seo,
    jsonLd: buildAboutJsonLd({ image: inaPortrait }),
  });

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navigation />
      <main className="pt-32 md:pt-40 pb-24 flex-1">
        <div className="container mx-auto px-4 sm:px-8 max-w-6xl">
          {/* Hero intro section with portrait */}
          <div className="max-w-4xl mb-20 md:mb-28 flex flex-col gap-8 md:gap-10">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-normal text-foreground leading-snug md:leading-snug tracking-tight">
              I'm Ina, a freelance portrait, lifestyle and interior photographer based in Stockholm.
            </h1>
            <img 
              src={inaPortrait} 
              alt="Ina Nederdal - Portrait photographer based in Stockholm"
              className="w-full max-w-2xl rounded-sm object-cover"
            />
          </div>

          {/* Two-column content */}
          <div className="max-w-4xl grid md:grid-cols-2 gap-16 md:gap-20">
            {/* Left column - About text */}
            <div className="space-y-6 text-foreground/85 leading-relaxed">
              <p>
                I love capturing my surroundings — people, places and other living things, both digitally and on film. I also work with graphic
                design, and when I have time over I enjoy making{" "}
                <Link
                  to="/work/collage"
                  className="border-b border-foreground/40 hover:border-foreground transition-colors"
                >
                  collages
                </Link>
                , both digital and by hand.
              </p>

              <p>
                Since becoming a mother in 2020, I've developed a passion for photographing other mothers with their
                children. Interested? Take a look at{" "}
                <Link
                  to="/hej-mamma"
                  className="border-b border-foreground/40 hover:border-foreground transition-colors"
                >
                  Hej Mamma
                </Link>
                .
              </p>

              <p>Things I like: interior design, going on adventures, art, sauna, thrifting.</p>
            </div>

            {/* Right column - Services & Contact */}
            <div className="space-y-10">
              <div>
                <h2 className="text-base font-small-heading font-extrabold italic text-foreground mb-4">
                  What I can do for you
                </h2>
                <p className="text-foreground/85 leading-relaxed">
                  <Link
                    to="/work/lifestyle"
                    className="border-b border-foreground/40 hover:border-foreground transition-colors"
                  >
                    Lifestyle
                  </Link>
                  ,{" "}
                  <Link
                    to="/work/interior"
                    className="border-b border-foreground/40 hover:border-foreground transition-colors"
                  >
                    interior
                  </Link>
                  ,{" "}
                  <Link
                    to="/work/commissions"
                    className="border-b border-foreground/40 hover:border-foreground transition-colors"
                  >
                    content creation
                  </Link>
                  ,{" "}
                  <Link
                    to="/work/portraits"
                    className="border-b border-foreground/40 hover:border-foreground transition-colors"
                  >
                    press photos
                  </Link>
                  ,{" "}
                  <Link
                    to="/work/event"
                    className="border-b border-foreground/40 hover:border-foreground transition-colors"
                  >
                    event documentation
                  </Link>
                  ,{" "}
                  <Link
                    to="/work/wedding"
                    className="border-b border-foreground/40 hover:border-foreground transition-colors"
                  >
                    weddings
                  </Link>
                  {" "}&{" "}
                  <Link
                    to="/hej-mamma"
                    className="border-b border-foreground/40 hover:border-foreground transition-colors"
                  >
                    family sessions
                  </Link>
                  .
                </p>
              </div>

              <div>
                <h2 className="text-base font-small-heading font-extrabold italic text-foreground mb-4">Contact</h2>
                <div className="space-y-2 text-foreground/85">
                  <p>
                    <a
                      href="mailto:ina.nederdal@gmail.com"
                      className="border-b border-foreground/40 hover:border-foreground transition-colors"
                    >
                      ina.nederdal@gmail.com
                    </a>
                  </p>
                  <p>
                    <a
                      href="https://www.instagram.com/inaouti/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="border-b border-foreground/40 hover:border-foreground transition-colors"
                    >
                      Instagram @inaouti
                    </a>
                  </p>
                  <p>
                    <Link
                      to="/booking"
                      className="border-b border-accent hover:border-accent/80 transition-colors"
                    >
                      Book a session →
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Clients section */}
          <div className="max-w-4xl mt-24 md:mt-32 pt-12 border-t border-foreground/10">
            <h2 className="text-base font-small-heading font-extrabold italic text-foreground mb-6">
              Clients/publications
            </h2>
            <p className="text-sm md:text-base text-foreground/60 leading-relaxed tracking-wide">
              BON Magazine · Odalisque Magazine · The Forumist · F Word Mag · TWWP · Smakexpeditionen · Måro · Nordiska
              Musikbyrån · Norma Stockholm · Charlie Tells · Betiyon · Nividas Eyewear
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default About;
